import { CloudUploadOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { v4 as uuidv4 } from "uuid";

import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { getPrivilege, updatePrivilege } from "../../services/crm.service";
import { uploadImage, viewImage } from "../../services/upload.service";
import { PrivilegeUpdate } from "../../types/crm";

type FormState = {
  name: string;
  state_privileges: string;
  points_required: string;
  total_permissions: string;
  per_user_limit: string;
  start_date: string;
  end_date: string;
  details: string;
};

const initialForm: FormState = {
  name: "",
  state_privileges: "",
  points_required: "",
  total_permissions: "",
  per_user_limit: "",
  start_date: "",
  end_date: "",
  details: "",
};

const requiredKeys: Array<keyof FormState> = [
  "name",
  "state_privileges",
  "points_required",
  "total_permissions",
  "per_user_limit",
  "start_date",
  "end_date",
  "details",
];

const stateOptions = [
  { label: "Active", value: "Active" },
  { label: "Draft", value: "Draft" },
  // { label: "Fully Redeemed", value: "Fully Redeemed" }, // controlled by backend
  { label: "Expired", value: "Expired" },
];

const getStateChipStyles = (value: string) => {
  if (value === "Active") {
    return { bgcolor: "#E3F5E3", color: "#2D6830" };
  }
  if (value === "Draft") {
    return { bgcolor: "#EDEEF1", color: "#25272C" };
  }
  if (value === "Fully Redeemed") {
    return { bgcolor: "#ECE9FE", color: "#662BD6" };
  }
  return { bgcolor: "#FFE3E1", color: "#BE2217" };
};

export default function CrmPrivilegesEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(initialForm);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [existingImageName, setExistingImageName] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Privilege List", to: "/crm/privileges" },
      { label: "Edit Privilege", to: `/crm/privileges/${id}/edit` },
    ]);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    const load = async () => {
      if (!id) {
        navigate("/crm/privileges");
        return;
      }

      setIsLoading(true);
      try {
        const privilege = await getPrivilege(id);
        setForm({
          name: privilege.name || "",
          state_privileges: privilege.category || "",
          points_required: String(privilege.points_required ?? ""),
          total_permissions: String(privilege.total_limit ?? ""),
          per_user_limit: String(privilege.per_user_limit ?? ""),
          start_date: privilege.start_date
            ? dayjs(privilege.start_date).format("YYYY-MM-DD")
            : "",
          end_date: privilege.end_date
            ? dayjs(privilege.end_date).format("YYYY-MM-DD")
            : "",
          details: privilege.details || "",
        });
        if (privilege.banner_image) {
          setExistingImageName(privilege.banner_image);
          setPreviewUrl(viewImage(privilege.banner_image));
        }
      } catch (error) {
        console.error("Error loading privilege:", error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const missing = useMemo(() => {
    if (!submitted) return new Set<string>();

    return new Set(
      requiredKeys.filter((key) => !String(form[key] || "").trim()).map(String),
    );
  }, [form, submitted]);

  const onFieldChange =
    (key: keyof FormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [key]: event.target.value }));
    };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file) {
      if (previewUrl && !existingImageName) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleDiscard = () => {
    navigate("/crm/privileges");
  };

  const handleSave = async () => {
    if (!id) return;

    setSubmitted(true);
    const hasMissing = requiredKeys.some(
      (key) => !String(form[key] || "").trim(),
    );
    if (hasMissing) return;

    setIsSubmitting(true);
    try {
      const payload: PrivilegeUpdate = {
        name: form.name.trim(),
        category: form.state_privileges.trim(),
        details: form.details.trim(),
        points_required: Number(form.points_required) || 0,
        per_user_limit: Number(form.per_user_limit) || 0,
        total_limit: Number(form.total_permissions) || 0,
        start_date: dayjs(form.start_date).toISOString(),
        end_date: dayjs(form.end_date).toISOString(),
      };

      if (selectedFile) {
        const fileExtension = selectedFile.name.split(".").pop();
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("file-name", `${uuidv4()}.${fileExtension}`);
        const uploadResponse = await uploadImage(formData);
        if (!uploadResponse?.fileName) {
          showSnackbar("Failed to upload image", "error");
          return;
        }
        payload.banner_image = uploadResponse.fileName;
      }

      await updatePrivilege(id, payload);
      showSnackbar("Privilege updated successfully", "success");
      navigate("/crm/privileges");
    } catch (error) {
      console.error("Error updating privilege:", error);
      showSnackbar("Failed to update privilege", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 320 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #D8DBDF" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
            Privilege Info
          </Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <TextField
                label={<>Privilege Name<span style={{ color: "#d32f2f" }}> *</span></>}
                placeholder="Enter Privilege Name"
                value={form.name}
                onChange={onFieldChange("name")}
                error={missing.has("name")}
                helperText={missing.has("name") ? "Required" : " "}
                fullWidth
              />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  select
                  label={<>Category<span style={{ color: "#d32f2f" }}> *</span></>}
                  placeholder="Select Category"
                  value={form.state_privileges}
                  onChange={onFieldChange("state_privileges")}
                  error={missing.has("state_privileges")}
                  helperText={
                    missing.has("state_privileges") ? "Required" : " "
                  }
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) return "Select Category";
                      const value = String(selected);
                      return (
                        <Chip
                          size="small"
                          label={value}
                          sx={{
                            borderRadius: "4px",
                            px: 0.5,
                            height: 24,
                            fontSize: 12,
                            ...getStateChipStyles(value),
                          }}
                        />
                      );
                    },
                  }}
                  fullWidth
                >
                  {stateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Chip
                        size="small"
                        label={option.label}
                        sx={{
                          borderRadius: "4px",
                          px: 0.5,
                          height: 24,
                          fontSize: 12,
                          ...getStateChipStyles(option.value),
                        }}
                      />
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={<>Points Required<span style={{ color: "#d32f2f" }}> *</span></>}
                  placeholder="Enter Points Required"
                  value={form.points_required}
                  onChange={onFieldChange("points_required")}
                  error={missing.has("points_required")}
                  helperText={missing.has("points_required") ? "Required" : " "}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label={<>Total Limit<span style={{ color: "#d32f2f" }}> *</span></>}
                  placeholder="Enter Total Limit"
                  value={form.total_permissions}
                  onChange={onFieldChange("total_permissions")}
                  error={missing.has("total_permissions")}
                  helperText={
                    missing.has("total_permissions") ? "Required" : " "
                  }
                  fullWidth
                />
                <TextField
                  label={<>Per User Limit<span style={{ color: "#d32f2f" }}> *</span></>}
                  placeholder="Enter Points Required"
                  value={form.per_user_limit}
                  onChange={onFieldChange("per_user_limit")}
                  error={missing.has("per_user_limit")}
                  helperText={missing.has("per_user_limit") ? "Required" : " "}
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label={<>Start Date<span style={{ color: "#d32f2f" }}> *</span></>}
                  type="date"
                  value={form.start_date}
                  onChange={onFieldChange("start_date")}
                  error={missing.has("start_date")}
                  helperText={missing.has("start_date") ? "Required" : " "}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
                <TextField
                  label={<>End Date<span style={{ color: "#d32f2f" }}> *</span></>}
                  type="date"
                  value={form.end_date}
                  onChange={onFieldChange("end_date")}
                  error={missing.has("end_date")}
                  helperText={missing.has("end_date") ? "Required" : " "}
                  slotProps={{ inputLabel: { shrink: true } }}
                  fullWidth
                />
              </Stack>

              <Box>
                <Typography sx={{ fontSize: 14, mb: 0.75 }}>
                  Upload Privilege Image<span style={{ color: "#d32f2f" }}> *</span>
                </Typography>

                {/* Current image preview */}
                {previewUrl && (
                  <Box
                    sx={{
                      mb: 1.5,
                      borderRadius: 1,
                      overflow: "hidden",
                      border: "1px solid #D8DBDF",
                      bgcolor: "#FFFFFF",
                      display: "inline-block",
                    }}
                  >
                    <Box
                      component="img"
                      src={previewUrl}
                      alt={selectedFile?.name ?? "Privilege image"}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.style.display = "none";
                      }}
                      sx={{
                        display: "block",
                        maxWidth: "100%",
                        width: 200,
                        height: 130,
                        objectFit: "cover",
                      }}
                    />
                    {selectedFile && (
                      <Typography sx={{ px: 1.5, py: 0.75, fontSize: 12, color: "#5F6D78" }}>
                        {selectedFile.name}
                      </Typography>
                    )}
                  </Box>
                )}

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderStyle: "dashed",
                    bgcolor: "#EDEEF1",
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#5F6D78" }}>
                    Drag and drop files here or click
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#9CADB6", mb: 2 }}>
                    {previewUrl ? "Click to replace image" : "Upload up to 1 files"}
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadOutlined />}
                    sx={{
                      borderRadius: "999px",
                      px: 3,
                      py: 0.75,
                      textTransform: "none",
                      bgcolor: "#111111",
                      "&:hover": { bgcolor: "#111111" },
                    }}
                  >
                    {previewUrl ? "Replace" : "Upload"}
                    <input hidden type="file" accept="image/*" onChange={onFileChange} />
                  </Button>
                </Paper>
              </Box>
            </Stack>

            <Box sx={{ flex: 1 }}>
              <TextField
                label={<>Privilege Details<span style={{ color: "#d32f2f" }}> *</span></>}
                placeholder="Please specify all the details required to use the privilege."
                multiline
                minRows={19}
                value={form.details}
                onChange={onFieldChange("details")}
                error={missing.has("details")}
                helperText={missing.has("details") ? "Required" : " "}
                fullWidth
              />
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ pt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={handleDiscard}
              sx={{
                borderRadius: "999px",
                px: 3,
                textTransform: "none",
                borderColor: "#111111",
                color: "#111111",
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSubmitting}
              sx={{
                borderRadius: "999px",
                px: 3,
                textTransform: "none",
                bgcolor: "#111111",
                "&:hover": { bgcolor: "#111111" },
              }}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
}
